import { t as localeText } from "@/shared/lib/translate";
import { useTranslation as useLocaleSubscription } from "react-i18next";
import { Children, useRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { ActionCard } from "./ActionCard";
import { Button } from "./Button";
import { Checkbox } from "./Choices";
import { Skeleton } from "./Display";
import { EmptyState } from "./EmptyState";
import { HeartIcon } from "./icons";

export interface AssetGalleryProps extends HTMLAttributes<HTMLDivElement> {
  label?: string;
  borderless?: boolean;
  mediaFit?: "cover" | "contain";
  state?: "ready" | "loading" | "empty" | "error";
  loadingLabel?: string;
  emptyState?: ReactNode;
  errorState?: ReactNode;
}

/** Present the current page of assets; callers own filtering, pagination and requests. */
export function AssetGallery({
  label = "Asset gallery",
  borderless = false,
  mediaFit = "cover",
  state = "ready",
  loadingLabel = "Loading assets…",
  emptyState,
  errorState,
  children,
  className,
  ...props
}: AssetGalleryProps) {
  useLocaleSubscription();
  const empty = state === "empty" || (state === "ready" && Children.toArray(children).length === 0);
  return (
    <div {...props} data-borderless={borderless || undefined} data-media-fit={mediaFit} className={cn("studio-asset-gallery", className)} aria-busy={state === "loading" || props["aria-busy"]}>
      {state === "loading" ? <span role="status" className="sr-only">{loadingLabel}</span> : null}
      {state === "error" ? (
        errorState ?? <EmptyState state="error" title={localeText("interface.assets_couldn_t_load_4210245e", { defaultValue: "Assets couldn’t load" })} />
      ) : empty ? (
        emptyState ?? <EmptyState title={localeText("interface.no_assets_yet_c55dddc4", { defaultValue: "No assets yet" })} className="bg-transparent" />
      ) : (
        <div role="list" aria-label={label} className="studio-asset-gallery-grid">
          {state === "loading" ? Array.from({ length: 6 }, (_, index) => (
            <div key={index} role="listitem" aria-hidden="true" className="studio-asset-gallery-skeleton">
              <Skeleton className="aspect-square w-full rounded-xl" />
              <Skeleton className="mt-3 h-3 w-2/3" />
            </div>
          )) : children}
        </div>
      )}
    </div>
  );
}

export interface AssetGalleryItemProps extends Omit<HTMLAttributes<HTMLElement>, "title" | "children"> {
  title: string;
  /** Image pickers can keep names in accessible labels without a caption row. */
  showCaption?: boolean;
  media: ReactNode;
  description?: ReactNode;
  status?: ReactNode;
  /** Sibling controls such as a menu; callers own their availability. */
  actions?: ReactNode;
  selected?: boolean;
  archived?: boolean;
  disabled?: boolean;
  favorite?: boolean;
  favoriteLabel?: string;
  previewLabel?: string;
  /** For pickers where the preview itself toggles selection. */
  previewPressed?: boolean;
  onPreview: () => void;
  /** Omit selection for a gallery that only supports previewing. */
  selection?: {
    label: string;
    disabled?: boolean;
    onChange: (checked: boolean, shiftKey: boolean) => void;
  };
}

/** Preview, selection and menu controls are siblings, never nested buttons. */
export function AssetGalleryItem({
  title,
  showCaption = true,
  media,
  description,
  status,
  actions,
  selected = false,
  archived = false,
  disabled = false,
  favorite = false,
  favoriteLabel = "Favorite",
  previewLabel = `Preview ${title}`,
  previewPressed,
  onPreview,
  selection,
  className,
  ...props
}: AssetGalleryItemProps) {
  const shiftPressed = useRef(false);
  return (
    <article {...props} role="listitem" className={cn("studio-asset-gallery-item", className)}
      data-selected={selected || undefined} data-archived={archived || undefined} data-disabled={disabled || undefined}>
      <div className="studio-asset-gallery-frame">
        <ActionCard variant="media" className="studio-asset-gallery-preview rounded-[inherit] border-0" aria-label={previewLabel} aria-pressed={previewPressed}
          disabled={disabled} onClick={onPreview}>
          {media}
        </ActionCard>
        {selection ? (
          <Checkbox hideLabel label={selection.label} checked={selected} disabled={disabled || selection.disabled}
            className="studio-asset-gallery-selection grid size-11 place-items-center p-0 sm:size-8 sm:min-h-8"
            onClick={event => { shiftPressed.current = event.shiftKey; }}
            onChange={event => selection.onChange(event.currentTarget.checked, shiftPressed.current)} />
        ) : null}
        {actions ? <div className="studio-asset-gallery-actions">{actions}</div> : null}
        {status ? <div className="studio-asset-gallery-status">{status}</div> : null}
      </div>
      {showCaption ? <div className="studio-asset-gallery-caption">
        <Button variant="ghost" size="sm" className="studio-asset-gallery-title min-w-0 flex-1 justify-start text-start text-ink-soft" disabled={disabled} onClick={onPreview}>
          <span className="truncate" title={title}>{title}</span>
        </Button>
        {favorite ? <span role="img" aria-label={favoriteLabel} className="studio-asset-gallery-favorite"><HeartIcon className="size-4" /></span> : null}
      </div> : null}
      {description ? <div className="studio-asset-gallery-description">{description}</div> : null}
    </article>
  );
}
